import { useTranslation } from "react-i18next";

import { Alert } from "@mantine/core";

import {
  ExtensionGateAlert,
  LanguageSwitcher,
  ShieldCheckIcon,
} from "@prostocheck/controls-web";
import { simpleResultErrorMessage } from "@prostocheck/i18n";
import { Card, Logo } from "@prostocheck/ui";

import { useLogin } from "./hooks";
import { LOGIN_STEPS } from "./login.constants";
import { LoginStepEnum } from "./login.models";
import { ConfirmStep, KeyStep, LoginProgress } from "./parts";

/**
 * Two steps, deliberately shaped like the tax cabinet: first prove the key can
 * be read, then sign in with it. The key is the only credential — we have no
 * accounts of our own — and it never leaves this page: it is decrypted in
 * memory and used to sign requests from the browser.
 *
 * A session dies with the tab, so this screen is seen often: one card, one
 * primary action, and the reassurance a page asking for a private key owes.
 */
export const LoginPage = () => {
  const { t } = useTranslation();
  const {
    step,
    form,
    authorities,
    owner,
    errorResult,
    isBusy,
    detectProgress,
    readKeyFile,
    signInWithKey,
    restart,
  } = useLogin();

  const errorAlert = errorResult && (
    <Alert color="red" variant="light" radius="md">
      {simpleResultErrorMessage({ result: errorResult, t })}
    </Alert>
  );

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center px-5 py-12">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-linear-to-b from-brand-50 to-surface-muted"
      />

      <Logo className="mb-8" />

      <div className="w-full max-w-md">
        <ExtensionGateAlert />
      </div>

      <Card className="w-full max-w-md sm:p-8">
        <LoginProgress steps={LOGIN_STEPS} activeStep={step} />

        {step === LoginStepEnum.Key ? (
          <KeyStep
            form={form}
            authorities={authorities}
            isBusy={isBusy}
            detectProgress={detectProgress}
            error={errorAlert}
            onSubmit={readKeyFile}
          />
        ) : (
          <ConfirmStep
            owner={owner}
            isBusy={isBusy}
            error={errorAlert}
            onBack={restart}
            onSubmit={signInWithKey}
          />
        )}
      </Card>

      <p className="mt-6 flex items-center gap-2 text-sm font-medium text-ink-700">
        <ShieldCheckIcon className="size-4 shrink-0 text-brand-600" />
        {t("login.trust")}
      </p>

      <p className="mt-2 max-w-md text-center text-xs text-pretty text-ink-500">
        {t("login.disclaimer")}
      </p>

      <LanguageSwitcher className="mt-7" />
    </div>
  );
};
